/**
 * UTM parameter capture and persistence.
 *
 * On first page load the module reads utm_* query parameters from the URL,
 * stores them in sessionStorage, and exposes them so they can be attached
 * as super-properties to every analytics event.
 */

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

const STORAGE_KEY = 'radix_utm_params';

/**
 * Read UTM params from the current URL query string and persist them
 * in sessionStorage. If params were already captured in this session
 * the stored values take precedence (first-touch within session).
 */
export function captureUtmParams(): UtmParams {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: UtmParams = {};
    let hasUtm = false;

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) {
        utm[key] = value;
        hasUtm = true;
      }
    }

    const stored = getStoredUtmParams();
    if (Object.keys(stored).length > 0) {
      return stored;
    }

    if (hasUtm) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
      return utm;
    }

    return {};
  } catch {
    return {};
  }
}

/**
 * Retrieve UTM params that were captured earlier in this session.
 */
export function getStoredUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UtmParams;
  } catch {
    return {};
  }
}

/**
 * Clear stored UTM params (useful for testing).
 */
export function clearUtmParams(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}
